import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import {
  CarrierAccountType,
  EasypostOAuthStatus,
  ICarrierAccount,
  RedoCarrierName,
  redoCarrierNameToCarrier,
  supportedCarriersForCarrierAccountType,
  UspsOAuthStatus,
} from "@redotech/redo-model/carrier-account/carrier-account";
import {
  Carrier,
  carrierToTrademarkText,
  Service,
} from "@redotech/redo-model/fulfillments/fulfillment-carriers-and-services";
import { RateProvider } from "@redotech/redo-model/outbound-labels/outbound-labels";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { RedoButton } from "../../../arbiter-components/buttons/redo-button";
import { RedoButtonDropdown } from "../../../arbiter-components/buttons/redo-dropdown-button";
import {
  RedoCommandMenu,
  RedoCommandMenuItem,
} from "../../../arbiter-components/command-menu/redo-command-menu";
import {
  RedoTable,
  RedoTableLayout,
} from "../../../arbiter-components/tables/redo-table";
import { RedoTablePaddedCell } from "../../../arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "../../../arbiter-components/tables/redo-table-header-cells";
import { RedoToggle } from "../../../arbiter-components/toggle/redo-toggle";
import { Flex } from "../../../flex";
import { LoadingRedoAnimation } from "../../../loading-redo-animation";
import { Text } from "../../../text";
import { LogoSize, ShippingLogo } from "../../common/shipping-logo";
import { AddCarrierModal } from "./add-carrier-modal";
import { CarrierInput, CarrierInputValue } from "./carrier-input";
import * as styles from "./carriers.module.css";

export interface ServiceLevelToggle {
  carrier: Carrier;
  service: Service;
  serviceDisplayName: string;
  enabled: boolean;
}

export interface CarrierServiceToggles {
  carrierAccountId?: string;
  carrier?: Carrier;
  serviceLevels: ServiceLevelToggle[];
  enabledCount: number;
  totalCount: number;
}

export const CarriersTable = memo(function CarriersTable({
  carrierAccountType,
  savedCarriers,
  carrierInputDefault,
  tableTitle,
  handleAuthenticateCarrier,
  handleDeleteCarrier,
  handleAddCarrier,
  createTestLabelPending,
  createTestLabelHandler,
  canAddDirectUspsOverride = false,
  onOpenFedexMfaModal,
  onUseMfaBackup,
  serviceToggles,
  onServiceLevelToggle,
  handleVerifyContractRates,
  verifyContractRatesPending,
}: {
  carrierAccountType: CarrierAccountType;
  savedCarriers: ICarrierAccount[] | null;
  carrierInputDefault: CarrierInputValue;
  tableTitle: string;
  handleAuthenticateCarrier: (carrierAccount: ICarrierAccount) => void;
  handleDeleteCarrier: (carrierAccount: ICarrierAccount) => void;
  handleAddCarrier: (
    carrier: CarrierInputValue,
  ) => Promise<{ success: boolean }>;
  createTestLabelPending?: boolean;
  createTestLabelHandler?: (carrierAccount: ICarrierAccount) => void;
  canAddDirectUspsOverride: boolean;
  onOpenFedexMfaModal?: () => void;
  onUseMfaBackup?: (carrierAccount: ICarrierAccount) => void;
  serviceToggles?: Map<string, CarrierServiceToggles>;
  onServiceLevelToggle?: (
    key: string,
    carrier: Carrier,
    service: Service,
    enabled: boolean,
  ) => void;
  handleVerifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>;
  verifyContractRatesPending?: boolean;
}) {
  const newCarrier = useInput(CarrierInput, carrierInputDefault);

  const [addCarrierModalOpen, setAddCarrierModalOpen] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState<RedoCarrierName>();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const onCloseAddCarrierModal = useHandler(() => {
    setAddCarrierModalOpen(false);
    setSelectedCarrier(undefined);
    newCarrier.setValue(carrierInputDefault);
  });

  const carrierOptions = useMemo(() => {
    const supported =
      supportedCarriersForCarrierAccountType(carrierAccountType);
    return supported;
  }, [carrierAccountType]);

  const toggleRowExpansion = useHandler((key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  });

  const rowsExpansions = useMemo(() => {
    if (!serviceToggles || !savedCarriers) return undefined;

    const expansions: Record<
      number,
      { expanded: boolean; numExpansions: number }
    > = {};
    for (const [index, carrier] of savedCarriers.entries()) {
      const key = carrier._id;
      const toggleData = serviceToggles.get(key);
      if (toggleData) {
        const isExpanded = expandedRows.has(key);
        expansions[index] = {
          expanded: isExpanded,
          numExpansions: isExpanded ? toggleData.serviceLevels.length : 0,
        };
      }
    }
    return expansions;
  }, [serviceToggles, savedCarriers, expandedRows]);

  return (
    <>
      <Flex align="flex-start" gap="none" justify="center" pt="3xl" w="full">
        <Flex dir="column" gap="none">
          <Flex
            bgColor="primary"
            borderBottomWidth="none"
            borderColor="primary"
            borderStyle="solid"
            borderWidth="1px"
            bottomLeftRadius="none"
            bottomRightRadius="none"
            gap="none"
            radius="sm"
          >
            <Flex dir="row" justify="space-between" p="xl" w="full">
              <Text fontSize="md" fontWeight="semibold" textColor="primary">
                {tableTitle}
              </Text>
              <RedoButton
                hierarchy="secondary"
                onClick={() => setAddCarrierModalOpen(true)}
                text="+ Add new carrier account"
              />
            </Flex>
          </Flex>
          <RedoTable
            additionalClasses={styles.standaloneTable}
            emptyStateContent={
              <EmptyStateContent
                carrierAccountType={carrierAccountType}
                savedCarriers={savedCarriers}
              />
            }
            layout={carriersTableLayout(
              handleAuthenticateCarrier,
              handleDeleteCarrier,
              carrierAccountType,
              createTestLabelPending,
              createTestLabelHandler,
              onUseMfaBackup,
              handleVerifyContractRates,
              verifyContractRatesPending,
              serviceToggles,
              toggleRowExpansion,
              expandedRows,
              onServiceLevelToggle,
            )}
            rowsExpansions={rowsExpansions}
            rowsOfData={savedCarriers ?? []}
            size="md"
          />
        </Flex>
      </Flex>
      <AddCarrierModal
        canAddDirectUspsOverride={canAddDirectUspsOverride ?? false}
        carrierAccountType={carrierAccountType}
        carrierOptions={carrierOptions}
        isOpen={addCarrierModalOpen}
        newCarrier={newCarrier}
        onAddCarrier={handleAddCarrier}
        onClose={onCloseAddCarrierModal}
        onOpenFedexMfaModal={onOpenFedexMfaModal}
        selectedCarrier={selectedCarrier}
        setSelectedCarrier={setSelectedCarrier}
      />
    </>
  );
});

const EmptyStateContent = memo(function EmptyStateContent({
  savedCarriers,
  carrierAccountType,
}: {
  savedCarriers: ICarrierAccount[] | null;
  carrierAccountType: CarrierAccountType;
}) {
  if (savedCarriers?.length) {
    return undefined;
  }

  return (
    <Flex
      align="center"
      bgColor="primary"
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      bottomLeftRadius="sm"
      bottomRightRadius="sm"
      dir="column"
      justify="flex-start"
      px="2xl"
      py="2xl"
      style={{ width: "100%", marginTop: "-5px", zIndex: 1 }}
    >
      {!savedCarriers ? (
        <LoadingRedoAnimation className={styles.loadingSpinner} />
      ) : (
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          {(() => {
            switch (carrierAccountType) {
              case CarrierAccountType.MERCHANT:
                return "No carriers found";
              case CarrierAccountType.REDO_CHILD:
                return "No Redo child carriers found";
              default:
                assertNever(carrierAccountType);
            }
          })()}
        </Text>
      )}
    </Flex>
  );
});

function carriersTableLayout(
  handleAuthenticateCarrier: (carrierAccount: ICarrierAccount) => void,
  handleDeleteCarrier: (carrierAccount: ICarrierAccount) => void,
  carrierAccountType: CarrierAccountType,
  createTestLabelPending?: boolean,
  createTestLabelHandler?: (carrierAccount: ICarrierAccount) => void,
  onUseMfaBackup?: (carrierAccount: ICarrierAccount) => void,
  handleVerifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>,
  verifyContractRatesPending?: boolean,
  serviceToggles?: Map<string, CarrierServiceToggles>,
  toggleRowExpansion?: (key: string) => void,
  expandedRows?: Set<string>,
  onServiceLevelToggle?: (
    key: string,
    carrier: Carrier,
    service: Service,
    enabled: boolean,
  ) => void,
): RedoTableLayout<ICarrierAccount> {
  const hasServiceToggles =
    serviceToggles && toggleRowExpansion && expandedRows;

  const columns: RedoTableLayout<ICarrierAccount>["columns"] = [
    ...(hasServiceToggles
      ? [
          {
            key: "expand",
            widthMode: { type: "fixed" as const, width: "40px" },
            renderHeaderCell: () => (
              <StandardRedoTableHeaderCell headerText="" />
            ),
            renderNormalCell: ({ row }: { row: ICarrierAccount }) => {
              const isExpanded = expandedRows.has(row._id);
              return (
                <RedoTablePaddedCell>
                  <Flex
                    align="center"
                    justify="center"
                    onClick={() => toggleRowExpansion(row._id)}
                    style={{ cursor: "pointer" }}
                    w="full"
                  >
                    {isExpanded ? (
                      <ChevronDownIcon height={16} width={16} />
                    ) : (
                      <ChevronRightIcon height={16} width={16} />
                    )}
                  </Flex>
                </RedoTablePaddedCell>
              );
            },
          },
        ]
      : []),

    {
      key: "name",
      widthMode: { type: "fixed" as const, width: "400px" },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Name" />,
      renderNormalCell: ({ row }: { row: ICarrierAccount }) => {
        const carrier = redoCarrierNameToCarrier[row.redoCarrierName];
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const carrierTrademarkText = carrierToTrademarkText[carrier];

        const isUspsDirectAccount = row.provider === RateProvider.USPS;
        const contractRatesLinked =
          row.provider === RateProvider.USPS && row.contractRatesLinked;

        return (
          <RedoTablePaddedCell>
            <Flex align="center" dir="row" gap="lg">
              {/* @ts-expect-error TODO: noUncheckedIndexedAccess FIXME */}
              <ShippingLogo carrier={carrier} size={LogoSize.MEDIUM} />
              <Flex dir="column" gap="none" py="sm">
                <Text fontWeight="semibold">{row.name}</Text>
                {carrierTrademarkText && (
                  <Text
                    fontSize="xxs"
                    pr="sm"
                    style={{ textWrap: "wrap" }}
                    textColor="tertiary"
                  >
                    {carrierTrademarkText}
                  </Text>
                )}
                {isUspsDirectAccount && (
                  <Text
                    fontSize="xxs"
                    textColor={contractRatesLinked ? "success" : "error"}
                  >
                    Contract rates verified:{" "}
                    {contractRatesLinked ? "true" : "false"}
                  </Text>
                )}
              </Flex>
            </Flex>
          </RedoTablePaddedCell>
        );
      },
      renderExpandedCell: serviceToggles
        ? ({
            row,
            expansionIndex,
          }: {
            row: ICarrierAccount;
            expansionIndex: number;
          }) => {
            const toggleData = serviceToggles.get(row._id);
            if (!toggleData) return null;
            const serviceLevel = toggleData.serviceLevels[expansionIndex];
            if (!serviceLevel) return null;
            return (
              <RedoTablePaddedCell>
                <Flex align="center" pl="3xl">
                  <Text fontSize="sm">{serviceLevel.serviceDisplayName}</Text>
                </Flex>
              </RedoTablePaddedCell>
            );
          }
        : undefined,
    },
    ...(hasServiceToggles
      ? [
          {
            key: "services",
            widthMode: { type: "fixed" as const, width: "140px" },
            renderHeaderCell: () => (
              <StandardRedoTableHeaderCell headerText="Services" />
            ),
            renderNormalCell: ({ row }: { row: ICarrierAccount }) => {
              const toggleData = serviceToggles.get(row._id);
              if (!toggleData) return null;
              return (
                <RedoTablePaddedCell>
                  <Text
                    fontSize="sm"
                    hoverTextColor="brand-primary"
                    onClick={() => toggleRowExpansion(row._id)}
                    style={{ cursor: "pointer" }}
                    textColor="tertiary"
                    textDecoration="underline"
                  >
                    {toggleData.enabledCount} of {toggleData.totalCount}
                  </Text>
                </RedoTablePaddedCell>
              );
            },
          },
        ]
      : []),

    {
      key: "download-test-label",
      widthMode: { type: "fixed" as const, width: "180px" },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
      renderNormalCell: ({ row }: { row: ICarrierAccount }) => {
        const isRedoFirstMile =
          carrierAccountType === CarrierAccountType.REDO_CHILD &&
          row.redoCarrierName === RedoCarrierName.FIRST_MILE;

        return (
          <RedoTablePaddedCell>
            {isRedoFirstMile && createTestLabelHandler && (
              <RedoButton
                hierarchy="primary"
                onClick={() => createTestLabelHandler(row)}
                pending={createTestLabelPending}
                size="sm"
                text="Download Test Label"
              />
            )}
          </RedoTablePaddedCell>
        );
      },
    },
    {
      key: "authenticate",
      widthMode: { type: "fixed" as const, width: "210px" },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
      renderNormalCell: ({ row }: { row: ICarrierAccount }) => {
        return (
          <RedoTablePaddedCell>
            <AuthenticateButton
              carrierAccount={row}
              handleAuthenticateCarrier={handleAuthenticateCarrier}
              handleVerifyContractRates={handleVerifyContractRates}
              onUseMfaBackup={onUseMfaBackup}
              verifyContractRatesPending={verifyContractRatesPending}
            />
          </RedoTablePaddedCell>
        );
      },
    },
    {
      key: "delete",
      widthMode: { type: "fixed" as const, width: "60px" },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
      renderNormalCell: ({ row }: { row: ICarrierAccount }) => (
        <RedoTablePaddedCell>
          <TrashIcon
            onClick={() => handleDeleteCarrier(row)}
            style={{ cursor: "pointer" }}
          />
        </RedoTablePaddedCell>
      ),
      renderExpandedCell:
        serviceToggles && onServiceLevelToggle
          ? ({
              row,
              expansionIndex,
            }: {
              row: ICarrierAccount;
              expansionIndex: number;
            }) => {
              const toggleData = serviceToggles.get(row._id);
              if (!toggleData) return null;
              const serviceLevel = toggleData.serviceLevels[expansionIndex];
              if (!serviceLevel) return null;
              return (
                <RedoTablePaddedCell>
                  <RedoToggle
                    setValue={(enabled) =>
                      onServiceLevelToggle(
                        row._id,
                        serviceLevel.carrier,
                        serviceLevel.service,
                        enabled,
                      )
                    }
                    value={serviceLevel.enabled}
                  />
                </RedoTablePaddedCell>
              );
            }
          : undefined,
    },
  ];

  return { columns };
}

function AuthenticateButton({
  carrierAccount,
  handleAuthenticateCarrier,
  handleVerifyContractRates,
  onUseMfaBackup,
  verifyContractRatesPending,
}: {
  carrierAccount: ICarrierAccount;
  handleAuthenticateCarrier: (carrierAccount: ICarrierAccount) => void;
  handleVerifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>;
  onUseMfaBackup?: (carrierAccount: ICarrierAccount) => void;
  verifyContractRatesPending?: boolean;
}) {
  const [commandMenuOpen, setCommandMenuOpen] = useState(false);
  const [commandMenuAnchor, setCommandMenuAnchor] =
    useState<HTMLElement | null>(null);

  const needsAuthentication =
    (carrierAccount.provider === RateProvider.EASYPOST &&
      carrierAccount.oauthUrl &&
      carrierAccount.oauthStatus !== EasypostOAuthStatus.APPROVED) ||
    (carrierAccount.provider === RateProvider.FEDEX &&
      !carrierAccount.authenticated) ||
    (carrierAccount.provider === RateProvider.USPS &&
      carrierAccount.oauthStatus !== UspsOAuthStatus.APPROVED);

  const canUseMfaBackup =
    carrierAccount.provider === RateProvider.FEDEX &&
    carrierAccount.canUseMfaBackup;

  // Always give an option to re-authenticate USPS carrier accounts. This is because USPS
  // refresh tokens expire after 7 days if we don't auto refresh, so we need to give the user
  // the option to re-authenticate as easily as possible just in case
  if (
    carrierAccount.provider === RateProvider.USPS &&
    carrierAccount.oauthStatus === UspsOAuthStatus.APPROVED
  ) {
    const commandMenuItems: RedoCommandMenuItem[] = [
      {
        text: "Verify Contract Rates",
        onClick: () => handleVerifyContractRates?.(carrierAccount),
      },
      {
        text: "Re-Authenticate",
        onClick: () => handleAuthenticateCarrier(carrierAccount),
      },
    ];

    return (
      <div ref={setCommandMenuAnchor}>
        <RedoButtonDropdown
          dropdownOpen={commandMenuOpen}
          hierarchy="secondary"
          onClick={() => handleVerifyContractRates?.(carrierAccount)}
          pending={verifyContractRatesPending}
          setDropdownOpen={setCommandMenuOpen}
          size="sm"
          text="Verify Contract Rates"
        >
          <RedoCommandMenu
            anchor={commandMenuAnchor}
            items={commandMenuItems}
            open={commandMenuOpen}
            setOpen={setCommandMenuOpen}
          />
        </RedoButtonDropdown>
      </div>
    );
  }
  return (
    <>
      {needsAuthentication && !canUseMfaBackup && (
        <RedoButton
          hierarchy="primary"
          onClick={() => handleAuthenticateCarrier(carrierAccount)}
          size="sm"
          text="Authenticate"
        />
      )}
      {needsAuthentication && canUseMfaBackup && (
        <RedoButton
          hierarchy="primary"
          onClick={() => onUseMfaBackup?.(carrierAccount)}
          size="sm"
          text="Use MFA Backup"
        />
      )}
    </>
  );
}
